import { useCallback, useEffect } from 'react'
import {
  useMeetingBroadcastingStore,
  selectBroadcastDurationSeconds,
  selectBroadcastPlatformLabel,
  type BroadcastConnection,
  type BroadcastPlatform,
} from '@/features/meeting/store/meeting-broadcasting.store'
import type { StartStreamInput } from '@/features/meeting/services/liveStream.api'

export type { BroadcastConnection, BroadcastPlatform }

const TICK_MS = 1000

/**
 * The meeting screen's view of the live stream.
 *
 * Takes the meeting id because every call is scoped to a meeting and the store
 * deliberately does not hold one: the store outlives any single meeting screen,
 * and a stale id there would stop the wrong broadcast.
 */
export function useBroadcasting(meetingId?: string) {
  const isLive = useMeetingBroadcastingStore((s) => s.isLive)
  const connection = useMeetingBroadcastingStore((s) => s.connection)
  const isBusy = useMeetingBroadcastingStore((s) => s.isBusy)
  const error = useMeetingBroadcastingStore((s) => s.error)
  const durationSeconds = useMeetingBroadcastingStore(selectBroadcastDurationSeconds)
  const platformLabel = useMeetingBroadcastingStore(selectBroadcastPlatformLabel)

  const setNow = useMeetingBroadcastingStore((s) => s.setNow)
  const startStream = useMeetingBroadcastingStore((s) => s.start)
  const stopStream = useMeetingBroadcastingStore((s) => s.stop)
  const endOnUnload = useMeetingBroadcastingStore((s) => s.endOnUnload)

  // Drives the elapsed-time readout only. The heartbeat that actually holds the
  // stream open runs in the store, so it survives this component unmounting.
  useEffect(() => {
    if (!isLive) return
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(id)
  }, [isLive, setNow])

  /**
   * End the stream when the page goes away.
   *
   * `pagehide` rather than `beforeunload`: it fires for a refresh, a close and
   * a back-navigation alike, and unlike `beforeunload` it also fires when the
   * page enters the back/forward cache on mobile Safari. This is only the fast
   * path — the mixer's lease ends the stream regardless, a few seconds later,
   * which is what covers a crash or a lost network where nothing fires at all.
   */
  useEffect(() => {
    if (!isLive || !meetingId) return
    const onHide = () => endOnUnload(meetingId)
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  }, [isLive, meetingId, endOnUnload])

  const start = useCallback(
    (input: StartStreamInput & { title?: string }) => {
      if (!meetingId) return Promise.resolve(false)
      return startStream(meetingId, input)
    },
    [meetingId, startStream],
  )

  const stop = useCallback(() => {
    if (!meetingId) return Promise.resolve()
    return stopStream(meetingId)
  }, [meetingId, stopStream])

  return {
    isLive,
    platformLabel,
    durationSeconds,
    connection,
    isBusy,
    error,
    start,
    stop,
  }
}
